import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import './App.css';
import axios from 'axios';
import HomeMain from './home/homeMain';
import AllTokenData from './home/allTokensData';
import io from 'socket.io-client';
import PerformanceSim from './home/performanceSim'; 
import Prediction from './home/prediction';
import FiveDayTokens from './home/fiveDayTokens';
import HistoryTokens from './home/historyTokens';
import HistoryData from './home/historyData';
import LoginSignup from './login';
import Header from './header';
import { AuthProvider } from './authContext'; 
import StoredTokens from './home/storedtokens';
import StoredData from './home/storedData';
import ProtectedRoute from './protectedRoute';
import PerformanceSimAll from './home/performanceAll';
import ContractScanner from './home/contractScanner';
import ContractScannerAuto from './home/contractScannerAuto';
import AiTradeSuggestionList from './ai/SingleTradeSuggestion';
import CustomTokenAnalyzer from './ai/CustomTokenAnalyzer';
import OutcomeEditor from './ai/EditTrades';
import AskAi from './ai/AskAi';
import AiCompletedStatsDashboard from './ai/aiCompletedDashboard';
import PortfolioDashboard from './user/Dashboard';
import AiChat from './user/AiChat';
import WalletDashboard from './binance/WalletDashboard';
import BotSettings from './pages/binance-bot-settings';
import HyperBotSettings from './pages/hyper-bot-settings';
import BitgetBotSettings from './pages/bitget-bot-settings';
import AdminOnly from './routes/AdminOnly';
import BitgetWalletDashboard from './bitget/BitgetWalletDashboard';
import UserFunds from './pages/UserFunds';
import AdminFundSharing from './pages/AdminFundSharing';
import AdminUserLedger from './pages/AdminUserLedger';
import BacktestMain from './ai/BacktestMain';
import GytisWalletDashboard from './gytis/GytisWalletDashboard';
import GytisBotSettings from './pages/gytis-bot-settings';
import AiTrackRecord from './ai/AiTrackRecord';
import MarketPulsePage from './pages/MarketPulsePage';
import MetalsDashboard from './metals/MetalsDashboard';
import MetalsEmbeds from './metals/MetalsEmbeds';
import HypeScanner from './hype/HypeScanner';
import CategoryList from './hype/CategoryList';
import CategoryDetail from './hype/CategoryDetail';
import MacroRegimePanel from './pages/MacroRegimePanel';
import MarketCoachPage from './aiCoach/MarketCoachPage';
import MarketEventsPage from './marketEvents/MarketEventsPage';
import HypeTrending from './hype/HypeTrending';
import { GlobalAiToastProvider } from './hype/GlobalAiToastProvider';

const isLocal = window.location.hostname === 'localhost';
const baseUrl = isLocal ? 'http://localhost:3000' : 'https://www.trendscout.club';
axios.defaults.baseURL = baseUrl;

// Initialize Socket.IO
const socket = io(baseUrl);

function App() {
  const [showNotification, setShowNotification] = useState(false);
  const [notificationMessage, setNotificationMessage] = useState('');
  const [showPutNotification, setShowPutNotification] = useState(false);
  const [putNotificationMessage, setPutNotificationMessage] = useState('');
  const [soundPlayed, setSoundPlayed] = useState(false);
  const [soundPlayedNew, setSoundPlayedNew] = useState(false);
  const [lastMoonshot, setLastMoonshot] = useState(null);
  const [recentPutNotifications, setRecentPutNotifications] = useState([]); // Track recent PUT notifications

  useEffect(() => {

    // Listen for the highest moonshot
    socket.on('moonshots', (moonshot) => {
      const { tokenName, priceChange } = moonshot; 
      const isSignificantChange = priceChange > 20;

      if ((!lastMoonshot && isSignificantChange) || 
          (lastMoonshot && lastMoonshot.tokenName !== tokenName && isSignificantChange) || 
          (lastMoonshot && lastMoonshot.priceChange !== priceChange && isSignificantChange)) {
      
        setNotificationMessage(`${tokenName} ${priceChange.toFixed(0)}%`);
        setShowNotification(true);

        if (!soundPlayed) {
          playSound(); 
          setSoundPlayed(true); 
        }

        setLastMoonshot({ tokenName, priceChange });
      }
    });

    // Listen for the PUT request detection
    socket.on('newDocument', (data) => {
      const putMessage = `${data.tokenName} MC $${data.priceChange.toLocaleString()}`;

      // Check if the message is in the recent notifications
      if (!recentPutNotifications.includes(putMessage)) {
        setPutNotificationMessage(putMessage);
        setShowPutNotification(true);
        
        if (!soundPlayedNew) {
          playSoundNew(); 
          setSoundPlayedNew(true); 
        }

        // Update recent notifications and keep track of the last few
        setRecentPutNotifications(prevNotifications => {
          const updatedNotifications = [...prevNotifications, putMessage];
          
          // Limit the number of tracked notifications to prevent memory bloat
          if (updatedNotifications.length > 5) {
            updatedNotifications.shift(); // Remove the oldest notification
          }

          return updatedNotifications;
        });
      }
    });

    // Cleanup the socket connection when the component unmounts
    return () => {
      socket.off('moonshots');
      socket.off('newDocument');
    };
  }, [soundPlayed, soundPlayedNew, lastMoonshot, recentPutNotifications]);

  // Function to play sound
  const playSound = () => {
    const audio = new Audio('/notification.mp3');
    audio.play().catch((error) => console.error('Error playing sound:', error));
  };

  // Function to play sound for PUT notifications
  const playSoundNew = () => {
    const audio = new Audio('/newtoken.mp3');
    audio.play().catch((error) => console.error('Error playing sound:', error));
  };

  // Function to dismiss the moonshot notification
  const dismissNotification = () => {
    setShowNotification(false);
    setSoundPlayed(false);
  };

  // Function to dismiss the PUT request notification
  const dismissPutNotification = () => {
    setShowPutNotification(false);
    setSoundPlayedNew(false); // Reset sound played flag on dismiss
  };

  return (
    <div className="App">
      <AuthProvider>
        <GlobalAiToastProvider>
        <Router>
          <Header />
          <Routes>
            <Route path="/" element={<HomeMain />} />
            <Route path="/login" element={<LoginSignup />} />
            
            {/* Protect all other routes */}
            <Route 
              path="/alltokens" 
              element={
                <ProtectedRoute>
                  <FiveDayTokens />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/all-token-data" 
              element={
                <ProtectedRoute>              
                  <AdminOnly>
                    <AllTokenData />
                  </AdminOnly>    
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/historical-tokens" 
              element={
                <ProtectedRoute>
                  <HistoryTokens />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/historical-token-data" 
              element={
                <ProtectedRoute>
                  <AdminOnly>
                    <HistoryData />
                  </AdminOnly>                  
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/stored-tokens" 
              element={
                <ProtectedRoute>
                  <StoredTokens />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/stored-token-data" 
              element={
                <ProtectedRoute>
                  <AdminOnly>
                   <StoredData />
                  </AdminOnly>          
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/performance-sim" 
              element={
                <ProtectedRoute>
                  <PerformanceSim />
                </ProtectedRoute>
              } 
            />
             <Route 
              path="/performance-sim-all" 
              element={
                <ProtectedRoute>
                  <PerformanceSimAll />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/prediction" 
              element={
                <ProtectedRoute>
                  <Prediction />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/security-check" 
              element={
                <ProtectedRoute>
                  <ContractScanner />
                </ProtectedRoute>
              } 
            />
             <Route 
              path="/security-check/auto/:contractAddress" 
              element={
                <ProtectedRoute>
                  <ContractScannerAuto />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/ai-trades" 
              element={
                <ProtectedRoute>
                  <AiTradeSuggestionList />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/ai-token-analysis" 
              element={
                <ProtectedRoute>
                  <AdminOnly>
                    <CustomTokenAnalyzer />
                  </AdminOnly>
                </ProtectedRoute>
              } 
            />
           <Route 
              path="/ai-update-tokens" 
              element={
                <ProtectedRoute>
                  <AdminOnly>
                    <OutcomeEditor />
                  </AdminOnly>
                </ProtectedRoute>
              } 
            />
           <Route 
              path="/ai-stats" 
              element={
                <ProtectedRoute>
                    <BacktestMain />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/ai-track-record" 
              element={
                <ProtectedRoute>
                    <AiTrackRecord />
                </ProtectedRoute>
              } 
            />
             <Route 
              path="/hyped-alts" 
              element={
                <ProtectedRoute>
                    <HypeScanner />
                </ProtectedRoute>
              } 
            />
             <Route 
              path="/ai-coach" 
              element={
                <ProtectedRoute>
                    <MarketCoachPage />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/market-events" 
              element={
                <ProtectedRoute>
                    <MarketEventsPage />
                </ProtectedRoute>
              } 
            />
           <Route 
              path="/macro-regime" 
              element={
                <ProtectedRoute>
                    <MacroRegimePanel />
                </ProtectedRoute>
              } 
            />
           <Route 
              path="/categories" 
              element={
                <ProtectedRoute>
                    <CategoryList />
                </ProtectedRoute>
              } 
            />
             <Route 
              path="/categories/:name" 
              element={
                <ProtectedRoute>
                    <CategoryDetail />
                </ProtectedRoute>
              } 
            />

             <Route 
              path="/precious-metals" 
              element={
                <ProtectedRoute>
                    <MetalsDashboard />
                </ProtectedRoute>
              } 
            />
             <Route 
              path="/embeds/metals" 
              element={
                <ProtectedRoute>
                    <MetalsEmbeds />
                </ProtectedRoute>
              } 
            />
            <Route 
                path="/binance/bot-settings" 
                element={
                  <ProtectedRoute>
                    <AdminOnly>
                      <BotSettings />
                    </AdminOnly>
                  </ProtectedRoute>
                }
              />
              <Route 
                path="/bitget/bot-settings" 
                element={
                  <ProtectedRoute>
                    <AdminOnly>
                      <BitgetBotSettings />
                    </AdminOnly>
                  </ProtectedRoute>
                }
              />
              <Route 
                path="/market-pulse/admin" 
                element={
                  <ProtectedRoute>
                    <AdminOnly>
                      <MarketPulsePage />
                    </AdminOnly>
                  </ProtectedRoute>
                }
              />
              <Route 
                path="/gytis/bot-settings" 
                element={
                  <ProtectedRoute>
                      <GytisBotSettings />
                  </ProtectedRoute>
                }
              />
              <Route 
                path="/hyper/bot-settings" 
                element={
                  <ProtectedRoute>
                    <AdminOnly>
                      <HyperBotSettings />
                    </AdminOnly>
                  </ProtectedRoute>
                }
              />
            <Route 
              path="/trendscout-chat-ai" 
              element={
                <ProtectedRoute>
                  <AskAi />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/ai-recommendation-stats" 
              element={
                <ProtectedRoute>
                  <AdminOnly>
                  <AiCompletedStatsDashboard />
                  </AdminOnly>
                </ProtectedRoute>
              } 
            />
           <Route 
              path="/dashboard" 
              element={
                <ProtectedRoute>
                  <PortfolioDashboard />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/ai-chat/00515166655gf4rr5df5v1f00055s" 
              element={
                <ProtectedRoute>
                   <AdminOnly>
                     <AiChat />
                   </AdminOnly>                 
                </ProtectedRoute>
              } 
            />
            <Route
                path="/gytis-bot-wallet"
                element={
                  <ProtectedRoute>
                      <GytisWalletDashboard />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/bitget-bot-wallet"
                element={
                  <ProtectedRoute>
                    <AdminOnly>
                      <BitgetWalletDashboard />
                    </AdminOnly>
                  </ProtectedRoute>
                }
              />
             <Route 
              path="/crg-wallet" 
              element={
                <ProtectedRoute>
                  <WalletDashboard />
                </ProtectedRoute>
              } 
            />
            <Route
              path="/me/funds"
              element={
                <ProtectedRoute>
                  <UserFunds />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/fund-sharing"
              element={
                <ProtectedRoute>
                  <AdminOnly>
                    <AdminFundSharing />
                  </AdminOnly>
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/fund-sharing/user/:id"
              element={
                <ProtectedRoute>
                  <AdminOnly>
                    <AdminUserLedger />
                  </AdminOnly>
                </ProtectedRoute>
              }
            />
          </Routes>
          <HypeTrending />
        </Router>
        </GlobalAiToastProvider>
      </AuthProvider>

      {showNotification && (
        <div className='notificationmain'>
          <div className={`notification ${showNotification ? 'show' : ''}`} onClick={dismissNotification}>
          <div className='messageText'>{notificationMessage}</div> 
            <img src="/up.gif" alt="Price Change Alert" style={{ width: '100px', height: '100px' }} />
        </div>
        </div>
      )}

      {showPutNotification && (
        <div className='notificationmain'>
          <div className={`notificationPut ${showPutNotification ? 'show' : ''}`} onClick={dismissPutNotification}>
            <div className='messageText'>{putNotificationMessage}</div>
            <img src="/new.gif" alt="New Token Alert" style={{ width: '100px', height: '100px' }} />
        </div>
        </div>
      )}
    </div>
  );  
}

export default App;
